/** Shared work-order assignee display and pre-execution inline editor. */
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { errorMessage } from "@/api/problem";
import { useAuth } from "@/app/auth";
import { PersonReference } from "@/features/entities/PersonReference";
import {
  type WorkOrder,
  useSetWorkOrderAssignees,
  useWorkOrderAssigneeOptions,
} from "@/features/workorders/queries";
import { SearchInput } from "@/shared/components/filter-bar";
import { InlineEditPopover } from "@/shared/components/inline-edit-popover";
import { Button } from "@/shared/ui/button";
import { Checkbox } from "@/shared/ui/form";

type Assignee = WorkOrder["assignees"][number];

const EDITABLE_STATUSES = new Set(["draft", "planned", "scheduled"]);

/** Display assigned people and allow planners to edit them before work starts. */
export function WorkOrderAssignees({
  workOrderId,
  status,
  assignees,
  compact = false,
}: {
  workOrderId: string;
  status: string;
  assignees: Assignee[];
  compact?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const [editing, setEditing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [search, setSearch] = useState<string>();
  const members = useWorkOrderAssigneeOptions(editing, search);
  const save = useSetWorkOrderAssignees(workOrderId);
  const editable =
    EDITABLE_STATUSES.has(status) && can("workorders.workorder.plan") && can("identity.user.view");
  const options = members.items;

  const changeOpen = (open: boolean): void => {
    if (save.isPending) return;
    if (open) {
      setSelectedIds(assignees.map((assignee) => assignee.membership_id));
      setSearch(undefined);
      save.reset();
    }
    setEditing(open);
  };

  const toggleMember = (membershipId: string, checked: boolean): void => {
    setSelectedIds((current) =>
      checked
        ? [...new Set([...current, membershipId])]
        : current.filter((id) => id !== membershipId),
    );
  };

  const submit = (): void => {
    save.mutate({ membership_ids: selectedIds }, { onSuccess: () => setEditing(false) });
  };

  return (
    <div className={compact ? "min-w-40" : undefined}>
      <div className="flex min-w-0 items-center gap-2">
        <span className="min-w-0 [overflow-wrap:anywhere]">
          {assignees.length
            ? assignees.map((assignee, index) => (
                <span key={assignee.membership_id}>
                  {index ? ", " : null}
                  <PersonReference
                    id={assignee.membership_id}
                    name={assignee.display_name}
                    context={{
                      kind: "work-order",
                      workOrderId,
                      assignmentRole: assignee.role,
                      onManageAssignment: editable ? () => changeOpen(true) : undefined,
                    }}
                  />
                </span>
              ))
            : t("workOrders.unassigned")}
        </span>
        {editable ? (
          <InlineEditPopover
            label={t("workOrders.editAssignees")}
            open={editing}
            onOpenChange={changeOpen}
            footer={
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={save.isPending}
                  onClick={() => setEditing(false)}
                >
                  {t("app.cancel")}
                </Button>
                <Button size="sm" disabled={save.isPending} onClick={submit}>
                  {save.isPending ? t("app.loading") : t("workOrders.saveAssignees")}
                </Button>
              </div>
            }
          >
            <div className="border-b p-3 [&>input]:w-full">
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder={t("workOrders.searchAssignees")}
              />
            </div>
            <div
              className="max-h-60 overflow-y-auto overscroll-contain p-1.5"
              role="group"
              aria-label={t("workOrders.assignees")}
              aria-busy={members.query.isFetching}
            >
              {members.query.isPending ? (
                <p className="text-sm text-muted-foreground">{t("app.loading")}</p>
              ) : members.query.isError ? (
                <p role="alert" className="text-sm text-destructive">
                  {errorMessage(members.query.error)}
                </p>
              ) : options.length ? (
                options.map((member) => {
                  return (
                    <Checkbox
                      key={member.id}
                      className="flex min-h-10 w-full cursor-pointer rounded-md px-2.5 py-2 hover:bg-accent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:checked]:bg-accent/60"
                      label={member.user.display_name || member.user.email}
                      disabled={save.isPending}
                      checked={selectedIds.includes(member.id)}
                      onChange={(event) => toggleMember(member.id, event.target.checked)}
                    />
                  );
                })
              ) : (
                <p className="text-sm text-muted-foreground">{t("workOrders.noAssigneeOptions")}</p>
              )}
              {members.hasMore ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={members.isLoadingMore}
                  onClick={members.loadMore}
                  className="mt-1 w-full"
                >
                  {t("workOrders.loadMoreAssignees")}
                </Button>
              ) : null}
            </div>
            {save.isError ? (
              <p role="alert" className="px-4 py-2 text-sm text-destructive">
                {errorMessage(save.error)}
              </p>
            ) : null}
          </InlineEditPopover>
        ) : null}
      </div>
    </div>
  );
}
